import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Assignment } from '../../../../api/app-types/assignment';
import { Client } from '../../../../api/app-types/client.app';
import { CreateDisabilityKind, DisabilityType } from '../../../../api/app-types/disability-type.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { DISABILITY_TYPE_SERVICE } from '../../../../api/services/disability-type.api';
import { provideUploadApi } from '../../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { matDateProviders } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { InfoTooltip } from '../../../../core/components/info-tooltip/info-tooltip';

@Component({
	selector: 'lvr-create-disability-type',
	imports: [
		AsyncPipe,
		TranslocoPipe,
		DatePipe,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatTooltip,
		MatDatepickerModule,
		MatButtonModule,
		LvrInputDirective,
		LvrAppendAsteriskPipe,
		DocumentUploadControl,
		LvrPaddingDirective,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrField,
		LvrHeading,
		LvrDefaultActions,
		LvrSearchableSelect,
		InfoTooltip,
	],
	providers: [provideUploadApi, matDateProviders],
	templateUrl: './create-disability-type.html',
	styleUrl: './create-disability-type.scss',
})
export class CreateDisabilityType extends LvrRedirectNav {
	public hasError = hasError;
	public readonly subjectType = DocumentFileSubjectType.DISABILITY_KIND;
	public readonly env = inject(ENVIRONMENT);
	private readonly transloco = inject(TranslocoService);
	private readonly fb = inject(FormBuilder);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	public readonly draftId = FORM_KEYS.CREATE_DISABILITY_TYPE;
	private readonly api = inject(DISABILITY_TYPE_SERVICE);
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved | undefined>('client');
	private readonly typesResponse = this.resolveDataAsSignal<PaginatedResponse<DisabilityType>>('disabilityTypes');
	private readonly assignment = this.resolveDataAsSignal<Assignment>('assignment');
	private readonly contactPhaseStartDate = computed(() => {
		const contactStart = this.assignment()?.contactStart;
		if (!contactStart) return undefined;

		const contactStartDate = new Date(contactStart);
		if (Number.isNaN(contactStartDate.getTime())) return undefined;

		contactStartDate.setHours(0, 0, 0, 0);
		return contactStartDate;
	});
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly types = computed(() => this.typesResponse()!.entities);
	public readonly canCreateDisabilityKindsPermission = hasPermission(
		this.session.permissions,
		'Documents.DisabilityKind.Create',
	);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly isLoading = signal<boolean>(false);

	public readonly form = this.initForm(this.fb);
	private readonly _selectedType = toSignal(
		this.form.get('disabilityType')!.valueChanges.pipe(map(() => this.form.get('disabilityType')!.value)),
	);
	public readonly subTypes = computed(() => {
		const selectedType = this._selectedType();
		if (!selectedType) return [];
		return this.types().find((t) => t.id === Number(selectedType))?.categoryDiagnoses ?? [];
	});
	public readonly subtypeLabel = (subtype: { description: string }) => subtype.description;
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly minStatusWeeks = this.env.typeSettings.minStatusWeeks; // 6 weeks
	public readonly maxStatusDate = signal(new Date());

	private readonly resetInvalidDifferentiation = effect(() => {
		const selectedType = this._selectedType();
		const selectedDifferentiation = this.form.controls.differentiation.value;
		const hasValidDifferentiation =
			selectedDifferentiation != null && this.subTypes().some((type) => type.id === Number(selectedDifferentiation));

		if (selectedType && hasValidDifferentiation) return;

		if (selectedDifferentiation != null) {
			this.form.controls.differentiation.setValue(undefined, { emitEvent: false });
		}
	});
	private readonly minStatusDateByWeeks = computed(() => {
		const max = this.maxStatusDate();
		const min = new Date(max);
		min.setDate(max.getDate() - this.minStatusWeeks * 7);
		min.setHours(0, 0, 0, 0);
		return min;
	});
	public readonly isStatusDateBoundByContactPhase = computed(() => {
		const contactPhaseStart = this.contactPhaseStartDate();
		if (!contactPhaseStart) return false;

		return contactPhaseStart > this.minStatusDateByWeeks();
	});
	public readonly minStatusDate = computed(() => {
		const minByWeeks = this.minStatusDateByWeeks();
		const contactPhaseStart = this.contactPhaseStartDate();
		if (contactPhaseStart && contactPhaseStart > minByWeeks) {
			return contactPhaseStart;
		}
		return minByWeeks;
	});
	public readonly minRecoveredOnYears = this.env.typeSettings.minRecoveredOnYears; // 2 years
	public readonly maxRecoveredOnDate = signal(new Date());
	public readonly minRecoveredOnDate = computed(() => {
		const max = this.maxRecoveredOnDate();
		const min = new Date(max);
		min.setFullYear(max.getFullYear() - this.minRecoveredOnYears);
		return min;
	});

	/**
	 * Cancel new disability type creation.
	 */
	public async cancel() {
		this.draftService.deleteDraft('client', this.draftId);
		await this.navigateAfterFinished(['/desk/client', this.client().id, 'disabilities']);
	}

	/**
	 * Submits the data in the form.
	 */
	public submitData = async () => {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const formData = this.form.value;
		const docs = formData.documents! as DocumentFile[];

		const data: CreateDisabilityKind = {
			categoryDisabilityTypeId: formData.disabilityType!,
			documentIds: docs.map((d: DocumentFile) => d.id),
			categoryDiagnoseId: formData.differentiation ?? undefined,
			statusAsOf: formData.statusAsOf?.getISODateStringWithoutTime() ?? '',
			disabledSince: formData.since ?? '',
			recoveredOn: formData.recoveredOn?.getISODateStringWithoutTime() ?? '',
			remark: formData.remark ?? '',
		};

		this.isLoading.set(true);

		try {
			await this.api.create(this.client().id, data);
			// Call the same as on cancel (save is done, so cleanup and redirect)
			await this.cancel();
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.disability_type_created')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.disability_type_create_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	};

	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	public differentiationInfoAriaLabel(description: string) {
		return `Info zu Ausdifferenzierung ${description}`;
	}

	public hasHintText(typeId: number): Observable<boolean> {
		const key = this.getInfoTextKey(typeId);
		return this.transloco.selectTranslate(key).pipe(map((translated) => translated !== key));
	}

	/**
	 * Initializes the form for creating a new Disability Type.
	 */
	private initForm(fb: FormBuilder) {
		return fb.group({
			disabilityType: fb.control<number | undefined>(undefined, [Validators.required]),
			differentiation: fb.control<number | undefined>(undefined, []),
			documents: fb.control<DocumentFile[]>(
				[],
				[
					maxFiles(this.env.typeSettings.maxFiles),
					maxFileSize(this.env.fileSettings.maxFileSize),
					allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
				],
			),
			statusAsOf: fb.control<Date | undefined>(undefined, [Validators.required]),
			since: fb.control<string | undefined>('', [Validators.maxLength(this.env.typeSettings.maxSinceLength)]),
			recoveredOn: fb.control<Date | undefined>(undefined),
			remark: fb.control<string | undefined>('', [Validators.maxLength(this.env.typeSettings.maxRemarkLength)]),
		});
	}

	public isLocalOrDevEnvironment(): boolean {
		const url = window.location.hostname;
		return url.includes('localhost') || url.includes('dev.');
	}

	public getInfoTextKey(typeId: number): string {
		if (this.isLocalOrDevEnvironment()) {
			return `client.disability.info_text_differentiation_local_development.${typeId}`;
		}
		return `client.disability.info_text_differentiation.${typeId}`;
	}
}
